import { useId } from "react";
import { CircleAlert, Check } from "lucide-react";
import { t } from "../i18n/t";
import { formatDateTime } from "../api/types";

/**
 * §B6.7, v2-feasibility-3: **props only**. It renders what it is given and calls nothing — no query, no mutation,
 * no `useMigration`. `src/screens/status/FailurePanel.tsx` (task 09) owns every fetch and passes these props.
 *
 * Redrawn as StatusFailed.html: the fail-tinted card with the icon tile, "This migration stopped", the
 * state/entered line, then eyebrow-labelled blocks (worker note, destination errors, preflight findings),
 * the retryable line, the request/job meta, and the actions row.
 *
 * `retryTarget` is rendered **verbatim** (v2-L-2): `set_state` writes `retry_target = <the state it failed from>`,
 * which may be `mapping`, `dry_running`, `loading` or anything else. This component never says "extracting".
 */
export type FailureCardProps = {
  state: string;
  stateLabel: string;
  /** The transition INTO this state ("Transforming → Failed"), from the newest event. */
  transitionLabel?: string;
  enteredAt: string | null;
  note: string | null;
  retryable: boolean | null;
  retryTarget: string | null;
  requestId: string | null;
  job: {
    id: string;
    status: string;
    statusLabel: string;
    attempts: number;
    deferredAt: string | null;
  } | null;
  batchId?: string | null;
  /**
   * Round-2 review fix (HIGH): the destination's OWN error lines (redacted at the write site, see
   * `screens/status/FailurePanel.tsx::destinationErrors`), not just the short `note` label. Empty when
   * the worker recorded no destination error text for this failure.
   */
  destinationErrors?: string[];
  /**
   * (critique C9) The newest `failed` event's own `payload.issues` -- the preflight report `stage_profile`
   * stores, formatted one line per issue. Before this, a migration blocked at preflight landed here with
   * only the generic note "preflight blocked" and no detail at all; the actionable rows sat on the Scope
   * screen, which is not where a blocked migration's Status screen sends the operator. Empty when the
   * newest event is not a preflight block.
   */
  preflightIssues?: string[];
  /**
   * The structured rendering of the same preflight report (plain-language title, "<Vendor> says / has" pair,
   * what to do next, inline override). The panel owns it because it owns the mutations; when present it is
   * shown INSTEAD of the raw `CODE: message` lines in `preflightIssues`.
   */
  preflightSection?: React.ReactNode;
  actions?: React.ReactNode;
  /**
   * (v4-M-6) The id the `<h2>` carries and `aria-labelledby` points at. It defaults to a `useId()` value, so two
   * cards on one page can never share an id — a hard-coded `id="failure-title"` produced a duplicate-id axe
   * violation the moment a second card rendered, and 09 renders one only by luck of the current layout.
   */
  titleId?: string;
};

export function FailureCard(props: FailureCardProps): JSX.Element {
  const generatedId = useId();
  const titleId = props.titleId ?? generatedId;
  const rollingBack = props.state === "rolling_back";
  return (
    <section className="card failcard" aria-labelledby={titleId}>
      <div className="failcard__head">
        <span className="failcard__icon" aria-hidden="true">
          <CircleAlert size={20} strokeWidth={2} />
        </span>
        <div>
          <h2 id={titleId} className="failcard__title">
            {t("failure.title")}
          </h2>
          <span className="failcard__sub">
            {t("failure.state_line", {
              state: props.transitionLabel ?? props.stateLabel,
              at: formatDateTime(props.enteredAt) || t("common.unknown"),
            })}
          </span>
        </div>
      </div>

      {props.note === null ? null : (
        <div className="block">
          <span className="eyebrow">{t("failure.note")}</span>
          <pre className="codeblock">{props.note}</pre>
        </div>
      )}
      {props.destinationErrors === undefined || props.destinationErrors.length === 0 ? null : (
        <div className="block">
          <span className="eyebrow">{t("failure.destination_errors")}</span>
          <pre className="codeblock">{props.destinationErrors.join("\n")}</pre>
        </div>
      )}
      {props.preflightSection !== undefined && props.preflightSection !== null ? (
        <div className="block">
          <span className="eyebrow">{t("failure.preflight_issues")}</span>
          {props.preflightSection}
        </div>
      ) : props.preflightIssues === undefined || props.preflightIssues.length === 0 ? null : (
        <div className="block">
          <span className="eyebrow">{t("failure.preflight_issues")}</span>
          <ul>
            {props.preflightIssues.map((issue, index) => (
              <li key={index}>{issue}</li>
            ))}
          </ul>
        </div>
      )}

      {rollingBack ? (
        <p>{t("failure.rolling_back", { batch: props.batchId ?? t("common.unknown") })}</p>
      ) : null}
      {props.state === "rolled_back" ? <p>{t("failure.rolled_back_retry")}</p> : null}

      {props.retryable === null ? null : props.retryable ? (
        <span className="checkline checkline--pass">
          <Check size={14} strokeWidth={2} aria-hidden="true" />
          {props.retryTarget === null
            ? t("failure.retryable_yes")
            : t("failure.retryable_line", { target: props.retryTarget })}
        </span>
      ) : (
        <p>{t("failure.retryable_no")}</p>
      )}

      {props.requestId === null ? null : (
        <span className="failcard__meta">{t("failure.request_id", { id: props.requestId })}</span>
      )}
      <span className="failcard__meta">
        {props.job === null
          ? t("failure.job_none")
          : t("failure.job", {
              id: props.job.id,
              status: props.job.statusLabel,
              attempts: props.job.attempts,
              deferred: formatDateTime(props.job.deferredAt) || t("common.unknown"),
            })}
      </span>

      {rollingBack ? null : <div className="failcard__actions">{props.actions}</div>}
    </section>
  );
}
